import React, { useState } from 'react'
import { TableCell } from '@mui/material'
import Box from '@src/components/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { Typography } from '@material-ui/core'
import HandleMarkup from '@src/components/HandleMarkup'
import { PricingAvailabilityItem } from '@src/aem-content/aem-types'
import clsx from 'clsx'
import { Field } from 'formik'
import LiquidEmeraldQuantityAdapter from '@src/components/LiquidEmeraldQuantityAdapter'
import LiquidQuantityInputAdapter from '@src/components/LiquidQuantityInputAdapter'
import DiscoPromoAddToCartButton, {
  Colors,
  Variants,
} from './DiscoPromoAddCartButton'

type DiscoPromoMaterialProps = {
  index: number
  material: PricingAvailabilityItem
  submitForm: () => void
  isSubmitting: boolean
  dirty: boolean
  canAddToCart: boolean
  hideAddToCartForPrepackItems: boolean
  isMarketplace?: boolean
  isMarketplaceCartEnabled: boolean
}

export interface MultipleMinQtyErrorsType {
  error: boolean
  name: string
}

const useStyles = makeStyles((theme: Theme) => ({
  materialNumberText: {
    fontSize: theme.typography.pxToRem(14),
  },
  emptyCell: {
    [theme.breakpoints.up('md')]: {
      width: '13%',
      minWidth: 120,
    },
  },
  sku: {
    [theme.breakpoints.up('md')]: {
      width: '22%',
      minWidth: 180,
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
  },
  packSize: {
    [theme.breakpoints.up('md')]: {
      width: '17%',
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
  },
  priceText: {
    width: '18%',
    textAlign: 'center',
    borderTop: `1px solid ${theme.palette.grey[400]}`,
  },
  tCell: {
    verticalAlign: 'middle',
  },
  quantityText: {
    width: '15%',
    textAlign: 'center',
    borderTop: `1px solid ${theme.palette.grey[400]}`,
  },
  qtyInput: {
    fontSize: theme.typography.pxToRem(14),
    height: 32,
    maxWidth: theme.typography.pxToRem(102.5),
    minWidth: theme.typography.pxToRem(102.5),
    '& svg': {
      fill: theme.palette.primary.main,
    },
  },
  addToCartButtonCell: {
    width: '15%',
    borderTop: `1px solid ${theme.palette.grey[400]}`,
  },
  addToCartButton: {
    width: '189px',
    minWidth: '144px',
    padding: theme.spacing(2, 8),
    textAlign: 'end',
    margin: theme.spacing(2, 2, 2, 0),
    lineHeight: '24px',
    height: '40px',
  },
}))

const DiscoPromoMaterial = ({
  index,
  material,
  submitForm,
  isSubmitting,
  dirty,
  isMarketplace,
  isMarketplaceCartEnabled,
}: DiscoPromoMaterialProps) => {
  const classes = useStyles()
  const [minQtyErrors, setMinQtyErrors] = useState<Record<string, boolean>>({})

  /*  show List Price if List Price not available show Price */
  const effectivePrice = material.listPrice ?? material.price
  const currency = material.listPriceCurrency ?? material.currency

  const shouldDisabledAddToCartBtn = Object.values(minQtyErrors).some(Boolean)

  const onMinimumOrderQtyChange = (name: string, hasError: boolean) => {
    setMinQtyErrors((prev) => ({ ...prev, [name]: hasError }))
  }

  return (
    <>
      <TableCell className={classes.emptyCell}></TableCell>
      <TableCell className={classes.sku}>
        <Typography
          component="span"
          className={classes.materialNumberText}
          dangerouslySetInnerHTML={{ __html: material?.materialNumber }}
        />
      </TableCell>
      <TableCell className={classes.packSize}>
        <HandleMarkup value={material?.packageSize} />
      </TableCell>
      <TableCell className={classes.priceText}>
        {effectivePrice != null ? (
          <FormattedNumber
            style="currency"
            currencyDisplay="symbol"
            currency={currency}
            value={effectivePrice}
          />
        ) : (
          <FormattedMessage id="UNAVAILABLE" defaultMessage="Unavailable" />
        )}
      </TableCell>

      <TableCell className={clsx(classes.tCell, classes.quantityText)}>
        <Box>
          <>
            {material.minOrderQuantity && material.minOrderQuantity > 1 ? (
              <Field
                name={`${index}.quantity`}
                component={LiquidEmeraldQuantityAdapter}
                className={classes.qtyInput}
                data-testid={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                id={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                associatedComponent={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                materialNumber={material.materialNumber}
                minOrderQuantity={material.minOrderQuantity}
                setMultipleMinimumQuantityError={onMinimumOrderQtyChange}
              />
            ) : (
              <Field
                name={`${index}.quantity`}
                component={LiquidQuantityInputAdapter}
                className={classes.qtyInput}
                data-testid={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                id={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                associatedComponent={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                max={material?.availableQtyInStock ?? 9999}
              />
            )}
          </>
        </Box>
      </TableCell>
      <TableCell align="right" className={classes.addToCartButtonCell}>
        {(!isMarketplace || (isMarketplace && isMarketplaceCartEnabled)) && (
          <DiscoPromoAddToCartButton
            size="large"
            variant={Variants.Contained}
            addToCartButtonClass={classes.addToCartButton}
            disabled={shouldDisabledAddToCartBtn}
            dirty={dirty}
            handleAddToCart={(e) => {
              e.preventDefault()
              submitForm()
            }}
            isSubmitting={isSubmitting}
            color={Colors.Secondary}
          />
        )}
      </TableCell>
    </>
  )
}

export default DiscoPromoMaterial
